import { useEffect, useState } from "react";
import api, { fmtMoney } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { PageHeader } from "../components/common";
import { Activity, AlertTriangle, Wrench, Clock, TrendingUp, DollarSign } from "lucide-react";
import { Link } from "react-router-dom";

const KpiCard = ({ icon: Icon, label, desc, value, unit, tone = "primary", testid }) => (
  <div className="border bg-card p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} className={tone === "primary" ? "text-primary" : "text-muted-foreground"} />
      <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
    </div>
    <div className="text-3xl font-black tabular-nums leading-none">
      {value == null ? "—" : value}{unit && <span className="text-sm ml-1 text-muted-foreground">{unit}</span>}
    </div>
    <div className="text-[10px] text-muted-foreground mt-2">{desc}</div>
  </div>
);

export default function MntDashboard() {
  const { t } = useI18n();
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    api.get("/mnt/kpis", { params: { days: 90 } }).then((r) => setKpis(r.data)).catch(() => setKpis({}));
  }, []);

  const k = kpis || {};
  return (
    <div data-testid="mnt-dashboard-page">
      <PageHeader title={t("mnt.k.title")} subtitle={t("mnt.k.subtitle")} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiCard icon={TrendingUp} label={t("mnt.k.mtbf")} desc={t("mnt.k.mtbf.desc")} value={k.mtbf_hours} unit="h" testid="kpi-mtbf" />
        <KpiCard icon={Clock} label={t("mnt.k.mttr")} desc={t("mnt.k.mttr.desc")} value={k.mttr_hours} unit="h" testid="kpi-mttr" />
        <KpiCard icon={Activity} label={t("mnt.k.availability")} desc={t("mnt.k.availability.desc")} value={k.availability_pct} unit="%" testid="kpi-availability" />
        <KpiCard icon={DollarSign} label={t("mnt.k.cost")} desc={t("mnt.k.cost.desc")} value={fmtMoney(k.total_cost || 0)} tone="muted" testid="kpi-cost" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <KpiCard icon={AlertTriangle} label={t("mnt.k.failures")} desc="PM02 + PM04" value={k.failure_count} testid="kpi-failures" />
        <KpiCard icon={Clock} label={t("mnt.k.downtime")} desc="tech_completed + closed" value={k.downtime_hours} unit="h" testid="kpi-downtime" />
        <Link to="/mnt/notifications">
          <KpiCard icon={AlertTriangle} label={t("mnt.k.openNotifs")} desc={t("mnt.viewDetail")} value={k.open_notifications} testid="kpi-open-notifs" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border bg-card p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest mb-3">{t("mnt.k.byType")}</div>
          <table className="w-full text-xs">
            <tbody>
              {["PM01", "PM02", "PM03", "PM04"].map((tp) => (
                <tr key={tp} className="border-b last:border-0">
                  <td className="py-1.5 font-bold">{t(`mnt.wo.type.${tp}`)}</td>
                  <td className="py-1.5 text-right font-black tabular-nums" data-testid={`type-count-${tp}`}>{(k.by_type || {})[tp] || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border bg-card p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest mb-3">{t("mnt.k.byStatus")}</div>
          <table className="w-full text-xs">
            <tbody>
              {["created", "released", "in_progress", "tech_completed", "closed", "cancelled"].map((st) => (
                <tr key={st} className="border-b last:border-0">
                  <td className="py-1.5 font-bold">{t(`mnt.wo.status.${st}`)}</td>
                  <td className="py-1.5 text-right font-black tabular-nums" data-testid={`status-count-${st}`}>{(k.by_status || {})[st] || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
